import { useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { HttpError } from '@/shared/api/httpClient';
import { plataforma } from '@/platform';
import { authApi, type EstadoAuth, type ResumoMaquina } from '../api/authApi';
import { VerificacaoEmailPage } from './VerificacaoEmailPage';
import logoOficina from '../../../../assets/logo.png';

// Modos do formulário. A criação de conta NÃO acontece aqui — é feita
// pela landing pública (`/assinar`) ou automaticamente pelo webhook do
// Mercado Pago quando o pagamento aprova. O app desktop só autentica
// quem já tem conta e lida com recuperação de senha.
type Modo = 'login' | 'recover';

const URL_CADASTRO = 'https://oficinaagil.softjack.com.br/assinar';

type AguardandoVerificacao = {
  email: string;
  senha: string;
  nomeMaquina?: string;
};

type Props = {
  estado: EstadoAuth | null;
  onAutenticado: () => Promise<void>;
};

function obterMaquinasDoErro(err: unknown): ResumoMaquina[] | null {
  if (!(err instanceof HttpError)) return null;
  const detalhes = (err.payload as { detalhes?: { maquinas?: ResumoMaquina[] } } | null)?.detalhes;
  return detalhes?.maquinas ?? null;
}

/**
 * Chave do localStorage onde guardamos o e-mail do último login
 * bem-sucedido. Permite pré-preencher o campo "E-mail" na próxima abertura
 * da tela de login — economia de digitação no fluxo recorrente.
 *
 * Salvar só o e-mail (nunca a senha) mantém o login mais seguro.
 */
const CHAVE_ULTIMO_EMAIL = 'ultimo-email-logado';

export function LoginPage({ estado, onAutenticado }: Props) {
  const [modo, setModo] = useState<Modo>('login');
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_ULTIMO_EMAIL) ?? '';
    } catch {
      return '';
    }
  });
  const [senha, setSenha] = useState('');
  const [nomeMaquina, setNomeMaquina] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [maquinasParaRemover, setMaquinasParaRemover] = useState<ResumoMaquina[] | null>(null);
  const [aguardandoVerificacao, setAguardandoVerificacao] =
    useState<AguardandoVerificacao | null>(null);

  // Login offline exige conta cacheada no banco local — coisa que só o
  // desktop tem. No app web a autenticação é sempre contra o servidor:
  // sem ele não há dado para abrir, então oferecer "entrar offline"
  // seria prometer o que o navegador não pode cumprir.
  const suportaOffline = plataforma.capacidades.offline;
  const podeLoginOffline = suportaOffline && estado?.temConta === true;
  // Se o app já sabe que está offline (último contato com servidor é
  // antigo / nunca aconteceu), nem tenta a requisição online — vai
  // direto no hash local. Evita esperar timeout do fetch.
  const sabidamenteOffline =
    estado !== null && estado.status !== 'online' && podeLoginOffline;

  function limparMensagens() {
    setErro(null);
    setMensagem(null);
    setMaquinasParaRemover(null);
  }

  /**
   * Persiste o e-mail no localStorage para a próxima abertura. Chamado em
   * todos os caminhos de login bem-sucedido (online, offline, fallback).
   */
  function lembrarEmail(usado: string): void {
    try {
      const limpo = usado.trim();
      if (limpo) localStorage.setItem(CHAVE_ULTIMO_EMAIL, limpo);
    } catch {
      /* localStorage indisponível — ignora silenciosamente */
    }
  }

  async function tentarLogin() {
    // Atalho: app já sabe que está offline → bypassa fetch, valida
    // direto contra o hash local. Sem gastar timeout do servidor.
    if (sabidamenteOffline) {
      try {
        await authApi.loginOffline({ email: email.trim() || undefined, senha });
        lembrarEmail(email);
        await onAutenticado();
      } catch (errOffline) {
        if (errOffline instanceof HttpError && errOffline.payload?.mensagem) {
          setErro(errOffline.payload.mensagem);
        } else {
          setErro(errOffline instanceof Error ? errOffline.message : 'falha inesperada');
        }
      }
      return;
    }

    try {
      await authApi.login({
        email: email.trim(),
        senha,
        nomeMaquina: nomeMaquina.trim() || undefined,
      });
      lembrarEmail(email);
      await onAutenticado();
    } catch (err) {
      if (err instanceof HttpError && err.payload?.codigo === 'limite.maquinas') {
        const maquinas = obterMaquinasDoErro(err);
        if (maquinas) setMaquinasParaRemover(maquinas);
        setErro(err.payload.mensagem);
        return;
      }
      // Conta criada pela landing/MP mas ainda não verificou o email →
      // leva pra tela do código.
      if (err instanceof HttpError && err.payload?.codigo === 'conta.nao-verificada') {
        setAguardandoVerificacao({
          email: email.trim(),
          senha,
          nomeMaquina: nomeMaquina.trim() || undefined,
        });
        // Pede reenvio do código pro usuário não precisar caçar email antigo.
        await authApi.reenviarVerificacao({ email: email.trim() }).catch(() => undefined);
        return;
      }
      // Servidor inacessível → tenta login offline. Sempre tenta — se
      // não tiver conta cacheada, o próprio loginOffline devolve
      // 'offline.sem-conta' que aí mostramos pro usuário. No web não há
      // esse caminho: a rota nem existe no servidor, e insistir só
      // trocaria a mensagem clara de rede por um erro sem sentido.
      const semRede =
        err instanceof HttpError &&
        (err.payload?.codigo === 'rede.indisponivel' ||
          err.payload?.tipo === 'Indisponivel' ||
          err.status === 503 ||
          err.status === 0);
      if (semRede && suportaOffline) {
        try {
          await authApi.loginOffline({ email: email.trim() || undefined, senha });
          lembrarEmail(email);
          await onAutenticado();
          return;
        } catch (errOffline) {
          if (errOffline instanceof HttpError && errOffline.payload?.mensagem) {
            setErro(errOffline.payload.mensagem);
            return;
          }
          throw errOffline;
        }
      }
      throw err;
    }
  }

  async function aoSubmeter(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    limparMensagens();
    setCarregando(true);
    try {
      if (modo === 'login') {
        await tentarLogin();
      } else if (modo === 'recover') {
        const r = await authApi.recover({ email: email.trim() });
        setMensagem(r.mensagem);
      }
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'falha inesperada');
    } finally {
      setCarregando(false);
    }
  }

  if (aguardandoVerificacao) {
    return (
      <VerificacaoEmailPage
        email={aguardandoVerificacao.email}
        senha={aguardandoVerificacao.senha}
        nomeMaquina={aguardandoVerificacao.nomeMaquina}
        onVerificado={async () => {
          lembrarEmail(aguardandoVerificacao.email);
          setAguardandoVerificacao(null);
          await onAutenticado();
        }}
        onVoltar={() => {
          setAguardandoVerificacao(null);
          limparMensagens();
          setModo('login');
        }}
      />
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img
            src={logoOficina}
            alt="Oficina Ágil"
            style={{ display: 'block', width: '100%', maxWidth: 220, height: 'auto' }}
          />
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8, color: 'var(--color-text)' }}>
          {modo === 'login' ? 'Entre na sua conta' : 'Recuperar senha'}
        </h1>

        {/* O badge só faz sentido onde existe banco local para seguir sem
            internet. No web ele seria ruído — e sugeriria um recurso
            inexistente. */}
        {modo === 'login' && suportaOffline && (
          <div style={{ marginBottom: 16 }}>
            <BadgeOffline ativo={podeLoginOffline} />
          </div>
        )}

        {modo === 'login' && sabidamenteOffline && (
          <div
            style={{
              padding: 10,
              background: 'var(--color-warning-soft)',
              border: '1px solid var(--color-warning)',
              borderRadius: 6,
              color: 'var(--color-warning)',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            Sem conexão com o servidor — vamos validar sua senha localmente.
          </div>
        )}

        <form onSubmit={aoSubmeter} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field rotulo="Email" obrigatorio>
            {(id) => (
              <input
                id={id}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@exemplo.com"
                disabled={carregando}
                className="input"
              />
            )}
          </Field>

          {modo === 'login' && (
            <>
              <Field rotulo="Senha" obrigatorio>
                {(id) => (
                  <input
                    id={id}
                    type="password"
                    required
                    minLength={6}
                    autoComplete="current-password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    disabled={carregando}
                    className="input"
                  />
                )}
              </Field>

              <Field rotulo="Nome desta máquina (opcional)" dica='Ex.: "Balcão" ou "PC do dono"'>
                {(id) => (
                  <input
                    id={id}
                    type="text"
                    maxLength={80}
                    value={nomeMaquina}
                    onChange={(e) => setNomeMaquina(e.target.value)}
                    placeholder="Computador"
                    disabled={carregando}
                    className="input"
                  />
                )}
              </Field>
            </>
          )}

          {erro && (
            <div
              style={{
                padding: 12,
                background: 'var(--color-danger-soft)',
                border: '1px solid var(--color-danger)',
                borderRadius: 6,
                color: 'var(--color-danger)',
                fontSize: 13,
              }}
            >
              {erro}
            </div>
          )}

          {mensagem && (
            <div
              style={{
                padding: 12,
                background: 'var(--color-success-soft)',
                border: '1px solid var(--color-success)',
                borderRadius: 6,
                color: 'var(--color-success)',
                fontSize: 13,
              }}
            >
              {mensagem}
            </div>
          )}

          {maquinasParaRemover && (
            <div
              style={{
                padding: 12,
                background: 'var(--color-warning-soft)',
                border: '1px solid var(--color-warning)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <strong>Máquinas atualmente vinculadas:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {maquinasParaRemover.map((m) => (
                  <li key={m.id} style={{ marginBottom: 4 }}>
                    {m.nome || 'Sem nome'} ·{' '}
                    {m.ultimoLoginEm
                      ? new Date(m.ultimoLoginEm).toLocaleString('pt-BR')
                      : 'nunca acessou'}
                  </li>
                ))}
              </ul>
              <p style={{ margin: '8px 0 0 0', color: 'var(--color-warning)', fontSize: 12 }}>
                Para liberar uma vaga, abra "Minhas máquinas" em uma máquina já vinculada,
                ou fale com o suporte: jackson.torres@softjack.com.br
              </p>
            </div>
          )}

          <Button type="submit" variante="primary" disabled={carregando} style={{ marginTop: 8 }}>
            {carregando
              ? 'Aguarde...'
              : modo === 'login'
                ? sabidamenteOffline
                  ? 'Entrar offline'
                  : 'Entrar'
                : 'Enviar link de recuperação'}
          </Button>
        </form>

        <div
          style={{
            marginTop: 20,
            paddingTop: 20,
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: 13,
          }}
        >
          {modo === 'login' ? (
            <>
              <button
                type="button"
                onClick={() => {
                  limparMensagens();
                  setModo('recover');
                }}
                style={linkStyle}
              >
                Esqueci minha senha
              </button>
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 12,
                  borderTop: '1px solid var(--color-border)',
                  color: 'var(--color-text-muted)',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                Ainda não tem conta? O cadastro é feito no site —{' '}
                <a
                  href={URL_CADASTRO}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', fontWeight: 600 }}
                >
                  criar conta em softjack.com.br
                </a>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                limparMensagens();
                setModo('login');
              }}
              style={linkStyle}
            >
              ← Voltar para o login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BadgeOffline({ ativo }: { ativo: boolean }) {
  const cor = ativo
    ? {
        bg: 'var(--color-success-soft)',
        borda: 'var(--color-success)',
        texto: 'var(--color-success)',
        ponto: 'var(--color-success)',
      }
    : {
        bg: 'var(--color-danger-soft)',
        borda: 'var(--color-danger)',
        texto: 'var(--color-danger)',
        ponto: 'var(--color-danger)',
      };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px',
        background: cor.bg,
        border: `1px solid ${cor.borda}`,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        color: cor.texto,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: cor.ponto,
        }}
      />
      Informações para seguir offline {ativo ? 'ativas' : 'inativas'}
    </span>
  );
}

const linkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary)',
  textAlign: 'left',
  cursor: 'pointer',
  padding: 0,
  fontSize: 13,
};
